import type { Treaty } from '@elysiajs/eden'
import { queryOptions, useQuery as useTanstackQuery } from '@tanstack/vue-query'
import { api, useQuery } from '@/plugins/api2'
import { queryClient, queryPresets } from '@/plugins/tanstack'

/**
 * The request body of `QUERY /api/v1/alert`, derived from the server through eden — never redeclared
 * here, so a change to the server's query schema shows up as a type error at the call site. The
 * `NonNullable` matters: eden types the body as optional, and without it indexed access such as
 * `AlertQueryBody['include']` fails on the `undefined` half.
 */
export type AlertQueryBody = NonNullable<Parameters<typeof api.alert.query>[0]>

/**
 * `Treaty.Data` accepts the endpoint function itself — it unwraps the return type and awaits it
 * internally — so there is no need to go through `Awaited<ReturnType<…>>`.
 */
export type AlertQueryData = Treaty.Data<typeof api.alert.query>

export type Alert = AlertQueryData['entries'][number]

export const alertQueryKeys = {
  alert: {
    key: ['alert'] as const,
    pluralKey: ['alerts'] as const,
  },
  types: {
    key: ['alert-types'] as const,
  },
}

export function getAlertsQueryKey(body: AlertQueryBody = {}) {
  return [...alertQueryKeys.alert.pluralKey, body]
}

/**
 * Plain `queryOptions`, deliberately not a composable: ag-grid's server-side datasource calls this
 * imperatively from `getRows`, outside any Vue setup scope. Use `useAlertsQuery` for declarative
 * consumption inside components — both share the same key, so they share the same cache entry.
 */
export function getAlertsQueryOpts(body: AlertQueryBody = {}) {
  return queryOptions({
    queryKey: getAlertsQueryKey(body),
    queryFn: async ({ signal }) => {
      const { data, error } = await api.alert.query(body, { fetch: { signal } })
      if (error) {
        throw error.value
      }
      return data
    },
    ...queryPresets.shortLived,
  })
}

export function useAlertsQuery(body: AlertQueryBody = {}) {
  return useQuery(getAlertsQueryKey(body), () => {
    return api.alert.query(body)
  })
}

/** Body of `PATCH /api/v1/alert/:uuid/data` — the alert data fields plus the `send` flag. */
export type AlertDataInput = NonNullable<Parameters<ReturnType<typeof api.alert>['data']['patch']>[0]>

/** Body of `POST /api/v1/alert/send` — either an explicit list of uuids or `{ all: true }`. */
export type AlertSendInput = NonNullable<Parameters<typeof api.alert.send.post>[0]>

/**
 * Manually sends pending alerts. Pass `{ all: true }` to let the server resolve every pending uuid
 * itself, which is what the dashboard's bulk button does — no client-side page limit involved. The server records the logged-in user in `confirmedBy` and reports
 * one result per uuid, so a rejected alert (not pending) does not fail the whole batch.
 */
export async function sendAlerts(body: AlertSendInput) {
  const { data, error } = await api.alert.send.post(body)
  if (error) {
    throw error.value
  }
  await refreshAlertRelatedQueries()
  return data
}

export type AlertTypesData = Treaty.Data<typeof api.alert.type.get>

export type AlertTypeEntry = AlertTypesData['entries'][number]

export function getAlertTypesQueryKey() {
  return [...alertQueryKeys.types.key]
}

/**
 * The alert types come from the server rather than a constant here, so a type added to the seed
 * shows up in the form without a web release. The form still recognises `payment` and `reserve` by
 * name to decide which type-specific fields to show.
 */
export function getAlertTypesQueryOpts() {
  return queryOptions({
    queryKey: getAlertTypesQueryKey(),
    queryFn: async ({ signal }) => {
      const { data, error } = await api.alert.type.get({ fetch: { signal } })
      if (error) {
        throw error.value
      }
      return data
    },
    ...queryPresets.shortLived,
  })
}

export function useAlertTypesQuery() {
  return useTanstackQuery(getAlertTypesQueryOpts())
}

/** Body of `POST /api/v1/alert` — the alert data fields plus `type`, `validUntil` and `send`. */
export type AlertCreateInput = NonNullable<Parameters<typeof api.alert.post>[0]>

/**
 * Creates an alert by hand. The server rejects it with 400 when it would duplicate an existing one
 * under the same rules the generation job applies, so the caller must surface the error message.
 */
export async function createAlert(input: AlertCreateInput) {
  const { data, error } = await api.alert.post(input)
  if (error) {
    throw error.value
  }
  await refreshAlertRelatedQueries()
  return data
}

/** Fills in the missing data of an alert, optionally sending it in the same request. */
export async function updateAlertData(uuid: string, input: AlertDataInput) {
  const { data, error } = await api.alert({ uuid }).data.patch(input)
  if (error) {
    throw error.value
  }
  await refreshAlertRelatedQueries()
  return data
}

/**
 * Invalidates every alert list regardless of the filter it was keyed with. `invalidateQueries` rather
 * than `refetchQueries` so inactive lists are only refetched once something observes them again.
 */
export function refreshAlertRelatedQueries() {
  return queryClient.invalidateQueries({ queryKey: alertQueryKeys.alert.pluralKey })
}
